import { api } from "@/lib/api-client";

export type Agent = {
  id: string;
  name: string;
  description?: string;
  status: "draft" | "active" | "inactive" | "paused" | string;
  language?: string;
  total_calls?: number;
  total_minutes?: number;
  agent_type?: string;
  pipeline_version?: string;
  template?: string | null;
  template_name?: string | null;
  template_vertical?: string | null;
  flow?: string | null;
  stt_provider?: string;
  response_pace?: string;
  speculative_replies?: boolean;
  voice_provider?: string;
  voice_id?: string;
  voice_settings?: {
    tts_model?: string;
    [k: string]: unknown;
  };
  llm_provider?: string;
  llm_model?: string;
  system_prompt?: string;
  initial_message?: string;
  lead_criteria?: string;
  knowledge_base?: string | null;
  conversation_flow?: string | null;
  fallback_phone_number?: string;
  transfer_message?: string;
  voicemail_detection_enabled?: boolean;
  voicemail_message?: string;
  followup_sms_enabled?: boolean;
  followup_email_enabled?: boolean;
  followup_template?: string;
  followup_booking_link?: string;
  hot_lead_routing_enabled?: boolean;
  hot_lead_inapp_enabled?: boolean;
  hot_lead_email_enabled?: boolean;
  hot_lead_email_recipients?: string;
  hot_lead_sms_enabled?: boolean;
  hot_lead_sms_recipients?: string;
  multilingual?: boolean;
  record_calls?: boolean;
  created_at: string;
  updated_at: string;
};

export type CreateAgentInput = {
  name: string;
  description?: string;
  agent_type?: string;
  language?: string;
  pipeline_version?: string;
  stt_provider?: string;
  response_pace?: string;
  speculative_replies?: boolean;
  voice_provider?: string;
  voice_id?: string;
  /** Merged JSON blob; voice_name keeps the picked voice's display name. */
  voice_settings?: Record<string, unknown>;
  llm_provider?: string;
  llm_model?: string;
  system_prompt?: string;
  initial_message?: string;
  lead_criteria?: string;
  voicemail_detection_enabled?: boolean;
  voicemail_message?: string;
  followup_sms_enabled?: boolean;
  followup_email_enabled?: boolean;
  followup_template?: string;
  followup_booking_link?: string;
  hot_lead_routing_enabled?: boolean;
  hot_lead_inapp_enabled?: boolean;
  hot_lead_email_enabled?: boolean;
  hot_lead_email_recipients?: string;
  hot_lead_sms_enabled?: boolean;
  hot_lead_sms_recipients?: string;
  multilingual?: boolean;
  record_calls?: boolean;
  template?: string | null;
  flow?: string | null;
};

export type VoiceCatalogVoice = {
  voice_id: string;
  name: string;
  gender: string;
};

/** One voice from the live provider listing (GET /agents/provider-voices/). */
export type ProviderVoice = {
  voice_id: string;
  name: string;
  gender: string;
  accent: string;
  language: string;
  preview_url: string;
  /** Set for ElevenLabs Voice-Library voices — needed to add them to the account. */
  public_owner_id: string;
  source: "mine" | "library";
};

export type VoiceFilters = {
  search?: string;
  language?: string;
  accent?: string;
};

export type VoiceCatalog = Record<
  string,
  Record<string, VoiceCatalogVoice[]>
>;

export type ChatMessage = {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp?: string;
};

/** A tool/function call the agent made during a chat turn (for the test UI). */
export type ToolCall = {
  name: string;
  arguments?: unknown;
  result?: unknown;
  duration_ms?: number | null;
  error?: string | null;
};

export type Paginated<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export const agentsApi = {
  list: async () => {
    const res = await api.get<Paginated<Agent> | Agent[]>("/agents/");
    const data = res.data;
    return Array.isArray(data) ? data : data.results;
  },

  get: async (id: string) => {
    const res = await api.get<Agent>(`/agents/${id}/`);
    return res.data;
  },

  remove: async (id: string) => {
    await api.delete(`/agents/${id}/`);
  },

  deploy: async (id: string) => {
    const res = await api.post<Agent>(`/agents/${id}/deploy/`);
    return res.data;
  },

  duplicate: async (id: string) => {
    const res = await api.post<Agent>(`/agents/${id}/duplicate/`);
    return res.data;
  },

  create: async (data: CreateAgentInput) => {
    const res = await api.post<Agent>("/agents/", data);
    return res.data;
  },

  update: async (id: string, data: Partial<CreateAgentInput>) => {
    const res = await api.patch<Agent>(`/agents/${id}/`, data);
    return res.data;
  },

  voiceCatalog: async () => {
    const res = await api.get<{ catalog: VoiceCatalog }>(
      "/agents/voice-catalog/",
    );
    return res.data.catalog;
  },

  /** Live voice list from the provider's API (tenant's own key) — never
   * stored server-side beyond a short cache. For ElevenLabs this includes
   * the full public Voice Library, filtered server-side. */
  providerVoices: async (provider: string, filters: VoiceFilters = {}) => {
    const res = await api.get<{ voices: ProviderVoice[] }>(
      "/agents/provider-voices/",
      { params: { provider, ...filters } },
    );
    return res.data.voices;
  },

  /** Library voices must be added to the tenant's provider account before
   * calls can use them; returns the voice_id to store. */
  addProviderVoice: async (body: {
    provider: string;
    voice_id: string;
    public_owner_id: string;
    name: string;
  }) => {
    const res = await api.post<{ voice_id: string }>(
      "/agents/provider-voices/add/",
      body,
    );
    return res.data.voice_id;
  },

  chat: async (
    id: string,
    data: { message: string; conversation_id?: string },
  ) => {
    const res = await api.post<{
      message?: string;
      response?: string;
      conversation_id?: string;
      tool_calls?: ToolCall[];
      latency_ms?: number;
    }>(`/agents/${id}/chat/`, data);
    return res.data;
  },

  clearChat: async (id: string, conversation_id: string) => {
    await api.delete(`/agents/${id}/chat/clear/`, {
      data: { conversation_id },
    });
  },
};

export type AgentStats = {
  total_calls?: number;
  total_minutes?: number;
  avg_call_duration_seconds?: number;
  success_rate?: number;
  recent_calls?: unknown[];
};

export const agentStatsApi = {
  get: async (id: string) => {
    const res = await api.get<AgentStats>(`/agents/${id}/stats/`);
    return res.data;
  },
};

// ── Agent Functions (tools) ─────────────────────────────────────────────────
export type AgentFunction = {
  id: string;
  agent: string;
  name: string;
  description: string;
  execution_type: "webhook" | "integration" | "internal" | string;
  execution_config: Record<string, unknown>;
  parameters_schema: Record<string, unknown>;
  is_active: boolean;
  created_at?: string;
};

export type FunctionLog = {
  id: string;
  status: "ok" | "ssrf_blocked" | "error" | string;
  duration_ms: number;
  response_code?: number | null;
  response_body?: string | null;
  error_detail?: string | null;
  created_at: string;
};

export type FunctionPayload = {
  agent: string;
  name: string;
  description?: string;
  execution_type: string;
  execution_config: Record<string, unknown>;
  parameters_schema: Record<string, unknown>;
};

export const agentFunctionsApi = {
  list: async (agentId: string) => {
    const res = await api.get<Paginated<AgentFunction> | AgentFunction[]>(
      "/agents/functions/",
      { params: { agent: agentId } },
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (data: FunctionPayload) => {
    const res = await api.post<AgentFunction>("/agents/functions/", data);
    return res.data;
  },
  remove: async (id: string) => {
    await api.delete(`/agents/functions/${id}/`);
  },
  execute: async (id: string, inputArgs: Record<string, unknown>) => {
    const res = await api.post<{
      status: string;
      response_code?: number;
      response_body?: string;
      error_detail?: string;
      duration_ms?: number;
    }>(`/agents/functions/${id}/execute/`, { input_args: inputArgs });
    return res.data;
  },
  logs: async (id: string) => {
    const res = await api.get<FunctionLog[]>(`/agents/functions/${id}/logs/`);
    return res.data;
  },
};

// ── Agent Versions ──────────────────────────────────────────────────────────
export type AgentVersion = {
  id: string;
  version_number: number;
  status: "published" | "archived" | "draft" | string;
  published_at?: string | null;
  published_by?: string | null;
  change_summary?: string;
};

export const agentVersionsApi = {
  list: async (agentId: string) => {
    const res = await api.get<AgentVersion[]>(`/agents/${agentId}/versions/`);
    return res.data;
  },
  publish: async (agentId: string, changeSummary?: string) => {
    const res = await api.post<{ version: number; id?: string }>(
      `/agents/${agentId}/publish/`,
      { change_summary: changeSummary || "" },
    );
    return res.data;
  },
  rollback: async (
    agentId: string,
    versionId: string,
    changeSummary?: string,
  ) => {
    const res = await api.post<{ new_version: number }>(
      `/agents/${agentId}/rollback/`,
      { version_id: versionId, change_summary: changeSummary || "" },
    );
    return res.data;
  },
};

// ── Vertical Agent Templates ────────────────────────────────────────────────
export type AgentTemplate = {
  id: string;
  name: string;
  vertical: string;
  vertical_display: string;
  description: string;
  icon: string;
  default_system_prompt: string;
  default_initial_message: string;
  default_voice_provider: string;
  default_voice_id: string;
  default_language: string;
  is_active: boolean;
  sort_order: number;
  tenant?: string | null;
  is_platform: boolean;
  created_at?: string;
  updated_at?: string;
};

export type CreateAgentTemplateInput = {
  name: string;
  vertical: string;
  description?: string;
  icon?: string;
  default_system_prompt: string;
  default_initial_message?: string;
  default_voice_provider?: string;
  default_voice_id?: string;
  default_language?: string;
  is_active?: boolean;
  sort_order?: number;
};

export type GenerateTemplateInput = {
  business_description: string;
  use_case: string;
  vertical?: string;
  tone?: "friendly" | "professional" | "casual";
  language?: string;
};

export type GenerateTemplateResponse = {
  name: string;
  description: string;
  system_prompt: string;
  initial_message: string;
};

export const agentTemplatesApi = {
  list: async () => {
    const res = await api.get<Paginated<AgentTemplate> | AgentTemplate[]>(
      "/agents/agent-templates/",
    );
    const data = res.data;
    return Array.isArray(data) ? data : data.results;
  },
  get: async (id: string) => {
    const res = await api.get<AgentTemplate>(`/agents/agent-templates/${id}/`);
    return res.data;
  },
  create: async (data: CreateAgentTemplateInput) => {
    const res = await api.post<AgentTemplate>("/agents/agent-templates/", data);
    return res.data;
  },
  update: async (id: string, data: Partial<CreateAgentTemplateInput>) => {
    const res = await api.patch<AgentTemplate>(
      `/agents/agent-templates/${id}/`,
      data,
    );
    return res.data;
  },
  remove: async (id: string) => {
    await api.delete(`/agents/agent-templates/${id}/`);
  },
  generate: async (data: GenerateTemplateInput) => {
    const res = await api.post<GenerateTemplateResponse>(
      "/agents/agent-templates/generate/",
      data,
    );
    return res.data;
  },
};

// ── Voice (STT/TTS/initiate-call) ───────────────────────────────────────────
export const voiceApi = {
  transcribe: async (audio: Blob) => {
    const fd = new FormData();
    fd.append("audio", audio, "input.webm");
    const res = await api.post<{ text: string }>("/voice/transcribe/", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },
  synthesize: async (data: {
    text: string;
    voice_id?: string;
    tts_provider?: string;
    tts_model?: string;
  }) => {
    const res = await api.post<{
      audio_base64: string;
      content_type?: string;
    }>("/voice/synthesize/", data);
    return res.data;
  },
  initiateCall: async (agentId: string) => {
    const res = await api.post<{ call_id: string; ws_path: string }>(
      "/voice/initiate/",
      { agent_id: agentId },
    );
    return res.data;
  },
};
